import { ChevronDownIcon } from '@heroicons/react/24/outline'
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { renderNav } from 'stories/layouts/utils'

export interface HeaderNavItemType {
  key: string
  /** May carry HTML (the notification badges), so it is rendered as markup. */
  name: string
  href: string
  /** True while the item points at the page being shown. */
  active?: boolean
  /** Extra classes for the link itself, e.g. the configured "background" highlight. */
  className?: string
}

// Matches the `gap-x-1` on the row below: items are measured one at a time, so the
// gaps between them have to be added back by hand when working out what fits.
const GAP = 4
const CLOSE_DELAY_MS = 500

const LINK_CLASS =
  'rounded-lg font-variation-settings-600 px-3 py-2 my-2 text-gray-600 whitespace-nowrap transition-colors duration-150 hover:bg-slate-100 hover:text-gray-900'
const ACTIVE_CLASS = 'bg-shade-blue/10 text-shade-blue font-semibold'

const linkClassName = (item: HeaderNavItemType) =>
  `${LINK_CLASS} ${item.active ? ACTIVE_CLASS : ''} ${item.className || ''}`

const overflowLinkClassName = (item: HeaderNavItemType) =>
  `block px-4 py-2 text-[15px] whitespace-nowrap transition-colors duration-150 hover:bg-slate-100 hover:text-gray-900 ${
    item.active ? `${ACTIVE_CLASS} rounded-none` : 'text-gray-600'
  }`

/**
 * The top-header links, kept to a single row.
 *
 * Tenants configure their own menu and servicing adds more items on top, so on a
 * narrower window the row used to wrap onto two or three lines and push the page
 * down. Instead the links that fit are shown and the rest move into a "More" menu:
 * nothing is ever hidden, the header keeps one row's height at every width.
 *
 * What fits is measured, not guessed - the labels are tenant text, so their widths
 * are only known at runtime. A hidden copy of the full row gives each item's natural
 * width; the visible row then takes as many as the available width allows, leaving
 * room for the "More" button whenever anything is left over.
 */
export const HeaderNav = ({ items }: { items: HeaderNavItemType[] }) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const rowRef = useRef<HTMLDivElement | null>(null)
  const measureRef = useRef<HTMLDivElement | null>(null)
  const panelRef = useRef<HTMLDivElement | null>(null)
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const [visibleCount, setVisibleCount] = useState(items.length)
  const [isOpen, setIsOpen] = useState(false)
  const [isPinned, setIsPinned] = useState(false)
  const [maxHeight, setMaxHeight] = useState<number | undefined>(undefined)

  const { pathname } = useLocation()

  const fit = useCallback(() => {
    const row = rowRef.current
    const measure = measureRef.current
    if (!row || !measure) return

    const children = Array.from(measure.children) as HTMLElement[]
    if (!children.length) return
    // The last child of the hidden row is the "More" button, so it is measured too.
    const moreWidth = children[children.length - 1].getBoundingClientRect().width
    const widths = children.slice(0, -1).map((el) => el.getBoundingClientRect().width)
    const available = row.clientWidth

    let used = 0
    let count = 0
    widths.every((width) => {
      const next = used + width + (count ? GAP : 0)
      // Sub-pixel widths round against us, so allow a pixel of slack.
      if (next > available + 1) return false
      used = next
      count += 1
      return true
    })
    // Anything left over needs the "More" button, which needs room of its own.
    if (count < widths.length) {
      while (count > 0 && used + moreWidth + GAP > available + 1) {
        used -= widths[count - 1] + (count > 1 ? GAP : 0)
        count -= 1
      }
    }
    setVisibleCount(count)
  }, [])

  useLayoutEffect(fit, [fit, items])

  useEffect(() => {
    const row = rowRef.current
    if (!row || typeof ResizeObserver === 'undefined') return
    // The row is `flex-1`, so its width comes from the header, not from its own
    // contents - observing it cannot feed back into itself.
    const observer = new ResizeObserver(() => fit())
    observer.observe(row)
    return () => observer.disconnect()
  }, [fit])

  useEffect(() => {
    // Web fonts land after the first paint and change every label's width.
    const fonts: any = (document as any).fonts
    if (!fonts?.ready?.then) return
    let cancelled = false
    fonts.ready.then(() => {
      if (!cancelled) fit()
    })
    return () => {
      cancelled = true
    }
  }, [fit])

  const cancelPendingClose = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current)
      closeTimer.current = null
    }
  }, [])

  const close = useCallback(() => {
    cancelPendingClose()
    setIsPinned(false)
    setIsOpen(false)
  }, [cancelPendingClose])

  useEffect(() => cancelPendingClose, [cancelPendingClose])

  // Picking an item navigates; close so the panel is not left hanging over the page.
  useEffect(() => {
    close()
  }, [pathname, close])

  useEffect(() => {
    if (!isOpen) return

    const onPointerDown = (event: MouseEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) close()
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close()
    }

    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [isOpen, close])

  // A tenant with a long menu can overflow a short window; cap the panel at the
  // viewport and let it scroll from there.
  useEffect(() => {
    if (!isOpen) return

    const fitToViewport = () => {
      const top = panelRef.current?.getBoundingClientRect().top || 0
      setMaxHeight(Math.max(200, window.innerHeight - top - 16))
    }

    fitToViewport()
    window.addEventListener('resize', fitToViewport)
    return () => window.removeEventListener('resize', fitToViewport)
  }, [isOpen])

  const onMouseEnter = () => {
    cancelPendingClose()
    setIsOpen(true)
  }

  const onMouseLeave = () => {
    if (isPinned) return
    cancelPendingClose()
    closeTimer.current = setTimeout(() => setIsOpen(false), CLOSE_DELAY_MS)
  }

  const onToggle = () => {
    cancelPendingClose()
    if (isOpen && isPinned) {
      close()
      return
    }
    setIsOpen(true)
    setIsPinned(true)
  }

  const visible = items.slice(0, visibleCount)
  const hidden = items.slice(visibleCount)
  const hasActiveHidden = hidden.some((item) => item.active)

  const moreClassName = `${LINK_CLASS} inline-flex items-center gap-1 flex-shrink-0 ${
    hasActiveHidden || isOpen ? ACTIVE_CLASS : ''
  }`

  const renderMoreButton = (measuring = false) => (
    <button
      type="button"
      className={moreClassName}
      aria-haspopup="true"
      aria-expanded={isOpen}
      tabIndex={measuring ? -1 : undefined}
      onMouseEnter={measuring ? undefined : onMouseEnter}
      onClick={onToggle}
    >
      More
      {hidden.length > 0 && !measuring && <span className="sr-only">{` (${hidden.length} more pages)`}</span>}
      <ChevronDownIcon className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
    </button>
  )

  return (
    // Hovering a link must not open the panel, so only the button opens it - but the close
    // is scheduled from the whole wrapper, which is the one element that covers both the
    // button and the panel hanging below it.
    <div ref={wrapperRef} className="relative flex-1 min-w-0" onMouseLeave={onMouseLeave}>
      <div ref={rowRef} className="flex flex-nowrap items-center justify-end gap-x-1 overflow-hidden">
        {visible.map((item) => renderNav(item, `nav-${item.key}`, linkClassName(item)))}
        {hidden.length > 0 && renderMoreButton()}
      </div>

      {/* A hidden copy of the whole row, used only to measure natural widths. It is
          clipped by the wrapper so a long menu cannot widen the page. */}
      <div aria-hidden className="absolute inset-0 overflow-hidden invisible pointer-events-none">
        <div ref={measureRef} className="flex flex-nowrap items-center gap-x-1 w-max">
          {items.map((item) => (
            <span
              key={`measure-${item.key}`}
              className={linkClassName(item)}
              dangerouslySetInnerHTML={{ __html: item.name }}
            />
          ))}
          {renderMoreButton(true)}
        </div>
      </div>

      {isOpen && hidden.length > 0 && (
        <div className="absolute top-full right-0 pt-2 z-50" role="menu" onClick={close} onMouseEnter={cancelPendingClose}>
          <div
            ref={panelRef}
            className="min-w-[12rem] bg-white rounded-lg border border-line shadow-lg py-1.5 overflow-y-auto overscroll-contain"
            style={{ maxHeight }}
          >
            {hidden.map((item) => renderNav(item, `more-${item.key}`, overflowLinkClassName(item)))}
          </div>
        </div>
      )}
    </div>
  )
}
